import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { createReaderWheelGate, findScrollableReaderParent } from "../../features/readerPaging";

const isControl = (target) => target?.closest?.('button, input, textarea, select, a, [contenteditable], [data-record-export-exclude]');

export default function useReaderPaging({ panelRef, canvasRef, enabled, page, pageCount, spread, setPage, recordId, loading, immediateWheel = false }) {
  const current = useRef(null);
  current.current = { page, pageCount, spread, setPage };
  const previousPage = useRef(page);
  const [badgeVisible, setBadgeVisible] = useState(true);

  useEffect(() => {
    if (!enabled) return undefined;
    setBadgeVisible(true);
    const timer = setTimeout(() => setBadgeVisible(false), 1600);
    return () => clearTimeout(timer);
  }, [enabled, page, pageCount, spread, recordId]);

  useEffect(() => {
    if (previousPage.current !== page) {
      const forwards = page > previousPage.current;
      const canvas = canvasRef.current;
      const outer = panelRef.current?.closest("[data-record-scroll]");
      if (canvas) canvas.scrollTop = forwards ? 0 : canvas.scrollHeight;
      if (outer) outer.scrollTop = forwards ? 0 : outer.scrollHeight;
    }
    previousPage.current = page;
  }, [page, canvasRef, panelRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!enabled || !canvas) return undefined;
    // Paged PDF reading turns on the first wheel input, even a fraction of a
    // pixel. Keep the existing momentum lock to avoid flipping on every tail event.
    const gate = createReaderWheelGate({ threshold: immediateWheel ? 0 : 24 });
    const scrollBoundary = panelRef.current?.closest("[data-record-scroll]") || panelRef.current || canvas;
    // Include the empty reading-area margins, where users also place the cursor.
    const wheelTarget = scrollBoundary;
    const turn = (direction) => {
      const state = current.current;
      if (direction > 0 && state.page + state.spread >= state.pageCount) return;
      const next = Math.max(0, Math.min(state.pageCount - 1, state.page + direction * state.spread));
      if (next !== state.page) { state.page = next; state.setPage(next); }
    };
    const wheel = (event) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey ||
        isControl(event.target) || !event.deltaY || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      const direction = Math.sign(event.deltaY);
      if (findScrollableReaderParent(event.target, scrollBoundary, direction)) { gate.reset(); return; }
      event.preventDefault();
      const units = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? canvas.clientHeight || window.innerHeight : 1;
      const timestamp = Number.isFinite(event.timeStamp) ? event.timeStamp : performance.now();
      const change = gate.consume(event.deltaY * units, timestamp);
      if (change) turn(change);
    };
    let touch = null;
    const touchStart = (event) => {
      touch = null;
      if (event.touches.length !== 1 || isControl(event.target)) return;
      const point = event.touches[0];
      // Leave OS/browser edge navigation and pinch zoom untouched.
      if (point.clientX < 20 || point.clientX > window.innerWidth - 20) return;
      touch = { x: point.clientX, y: point.clientY,
        canScrollDown: Boolean(findScrollableReaderParent(event.target, scrollBoundary, 1)),
        canScrollUp: Boolean(findScrollableReaderParent(event.target, scrollBoundary, -1)),
      };
    };
    const touchMove = (event) => { if (event.touches.length !== 1) touch = null; };
    const touchEnd = (event) => {
      const start = touch;
      touch = null;
      if (!start || event.touches.length || !event.changedTouches.length) return;
      const point = event.changedTouches[0];
      const dx = start.x - point.clientX;
      const dy = start.y - point.clientY;
      if (Math.abs(dy) > 70 && Math.abs(dy) > Math.abs(dx) * 1.3) {
        if (dy > 0 ? start.canScrollDown : start.canScrollUp) return;
        turn(Math.sign(dy));
      } else if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.3 && canvas.scrollWidth <= canvas.clientWidth + 2) {
        turn(Math.sign(dx));
      }
    };
    const touchCancel = () => { touch = null; };
    wheelTarget.addEventListener("wheel", wheel, { passive: false });
    canvas.addEventListener("touchstart", touchStart, { passive: true });
    canvas.addEventListener("touchmove", touchMove, { passive: true });
    canvas.addEventListener("touchend", touchEnd, { passive: true });
    canvas.addEventListener("touchcancel", touchCancel, { passive: true });
    return () => {
      wheelTarget.removeEventListener("wheel", wheel);
      canvas.removeEventListener("touchstart", touchStart);
      canvas.removeEventListener("touchmove", touchMove);
      canvas.removeEventListener("touchend", touchEnd);
      canvas.removeEventListener("touchcancel", touchCancel);
    };
  }, [enabled, canvasRef, panelRef, recordId, immediateWheel]);

  if (!enabled || !badgeVisible) return null;
  const last = Math.min(pageCount, page + spread);
  // The handwriting surface is transformed; portal outside it to anchor to the viewport.
  return createPortal(<PageBadge role="status" aria-label="目前預覽頁碼" data-record-export-exclude="true">
    Page {page + 1}{last > page + 1 ? `–${last}` : ""} of {pageCount}{loading ? "+" : ""}
  </PageBadge>, panelRef.current?.closest('[role="dialog"]') || document.body);
}

const PageBadge = styled.div`
  position: fixed; top: max(10px, env(safe-area-inset-top)); left: 50%; transform: translateX(-50%);
  z-index: 1600; padding: 5px 12px; border-radius: 8px;
  background: rgba(70, 70, 70, .76); color: #fff; pointer-events: none;
  font: 500 clamp(15px, 2vw, 23px)/1.25 system-ui, sans-serif;
  white-space: nowrap; box-shadow: 0 2px 8px #0001;
  @media print { display: none; }
`;
